import type { ModuleDTO } from '@/features/api/extranet/dto/Demande/CPRO'
import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
import { BaseCalculFrais, ModeFrais, type NatureFrais } from '@/features/domaine/types/opco/Demande'
import {
  HelpFraisAnnexe,
  HelpFraisPedagogique,
  NatureFraisAvecQuantite,
  NaturesFrais,
} from '@/features/domaine/types/opco/Demande/CPRO'
import type { TypeModule } from '@/features/domaine/types/opco/Demande/shared/TypeModule'
import { useDemandeCPROStore } from '@/features/interface/demande/cpro/useDemandeCPROStore'
import type { FraisDataParMode } from '@/features/interface/demande/shared/components/frais/Frais.types'
import { useDialog } from '@/features/interface/dialog'
import type { IconName } from '@designSystem/fondation/icons/iconsList'
import { storeToRefs } from 'pinia'
import { computed, nextTick, ref, watch, type InjectionKey } from 'vue'
import { useAppContextStore, TypesContextesAutorisesEnum } from '@/features/auth/context-app'

type ModuleAction = ReturnType<typeof useModuleActions>
export const CPRO_MODULES_ACTIONS: InjectionKey<ModuleAction> = Symbol()

export interface ModuleConfig {
  limite: number
  label: string
  icon: IconName
}

export const MODULE_CONFIG: Record<TypeModule, ModuleConfig> = {
  Interne: {
    limite: 1,
    label: 'Interne',
    icon: 'building',
  },
  Externe: {
    limite: 5,
    label: 'Externe',
    icon: 'bank',
  },
}

export function useModuleActions() {
  const store = useDemandeCPROStore()
  const { model, readonly, estTT } = storeToRefs(store)
  const { setupCustomUpdater } = store
  const dialog = useDialog()

  const activeModuleIndex = ref<number | null>(null)
  const afficherModale = ref(false)

  const idScrollDownToSaisie = computed(() =>
    moduleEnCours.value?.typeModule === 'Externe' ? 'module-scroll-externe' : 'module-scroll-interne',
  )

  const modules = computed(() => model.value?.formation?.modules || [])

  const nombreModules = computed(() => ({
    Interne: modules.value.filter((module) => module.typeModule === MODULE_CONFIG.Interne.label).length,
    Externe: modules.value.filter((module) => module.typeModule === MODULE_CONFIG.Externe.label).length,
  }))

  const nbMaxModulesAtteint = computed(
    () =>
      nombreModules.value.Interne >= MODULE_CONFIG.Interne.limite &&
      nombreModules.value.Externe >= MODULE_CONFIG.Externe.limite,
  )

  const creerTypeModuleVide = (typeModule: TypeModule): ModuleDTO => {
    return {
      etablissementPrestataire: null,
      avecSubrogation: estTT.value && typeModule === 'Externe' ? false : null,
      modaliteFacturation: null,
      nombreLivrable: null,
      attestationMoyenRealisation: false,
      typeModule,
      modalitePedagogique: null,
      dureeFoad: null,
      duree: null,
      dateDebut: null,
      dateFin: null,
      dureeEnseignement: null,
      fraisPedagogiques: initialiseListeFrais('Pedagogique', typeModule),
      fraisAnnexesSalarie: initialiseListeFrais('Annexe', typeModule),
    } as ModuleDTO
  }

  function initialiseListeFrais(mode: ModeFrais, typeModule: TypeModule) {
    let natureFrais = NaturesFrais[mode]
    if (
      model.value?.sousDispositif !== 'Prodiat' ||
      (model.value?.sousDispositif === 'Prodiat' && typeModule === 'Interne')
    )
      natureFrais = natureFrais.filter((x) => x !== 'LivrableOfa')

    return natureFrais.map((nature) => {
      const baseCalcul: BaseCalculFrais = NatureFraisAvecQuantite.includes(nature) ? 'Quantite' : 'Montant'
      return {
        mode,
        nature,
        baseCalcul,
        montant: null,
        quantite: null,
      } as FraisDTO
    })
  }

  const patchAjouterModule = setupCustomUpdater<TypeModule>((patch, typeModule) => {
    const nouveauModule = creerTypeModuleVide(typeModule)

    if (!patch.formation?.modules) patch.formation!.modules = []
    patch.formation?.modules?.push(nouveauModule)
  })

  async function scrollDownToSaisie() {
    await nextTick()
    await nextTick()
    document.getElementById(idScrollDownToSaisie.value)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function définirModuleEnCours(index: number | null) {
    const isTogglingOff = index === activeModuleIndex.value
    activeModuleIndex.value = isTogglingOff ? null : index
    if (activeModuleIndex.value !== null) {
      scrollDownToSaisie()
    }
  }

  async function ajouterModule(typeModule: TypeModule) {
    await patchAjouterModule(typeModule)
    activeModuleIndex.value = model.value!.formation!.modules!.length - 1
    afficherModale.value = false
    définirModuleEnCours(activeModuleIndex.value)
  }

  async function supprimerModule(index: number) {
    const module = listeModules.value[index]

    const result = await dialog.showDialog({
      message: `Confirmez-vous la suppression du module de type ${module?.typeModule} ?`,
      type: 'YesNo',
      title: 'Supprimer un module',
    })

    if (result === 'Yes') {
      await supprimerModuleDuModel(index)

      if (activeModuleIndex.value === index) {
        activeModuleIndex.value = null
      } else if (activeModuleIndex.value !== null && activeModuleIndex.value > index) {
        activeModuleIndex.value -= 1
      }
    }
  }

  const supprimerModuleDuModel = setupCustomUpdater<number>((patch, index) => {
    const element = patch.formation?.modules?.splice(index, 1)
    if (!element) throw 'Index inexistant'
  })

  const listeModules = computed<ModuleDTO[]>(() => {
    return model.value!.formation!.modules!
  })

  const moduleEnCours = computed<ModuleDTO | undefined>(() => {
    return listeModules.value![activeModuleIndex.value!]
  })

  const metaModuleEnCours = computed(() => {
    if (typeof activeModuleIndex.value !== 'number') return null
    return store.meta.formation?.modules?.at(activeModuleIndex.value)
  })

  const fraisDataParMode = computed<FraisDataParMode>(() => {
    return {
      Pedagogique: {
        frais: moduleEnCours.value?.fraisPedagogiques || [],
        metaFrais: metaModuleEnCours.value?.fraisPedagogiques,
        fraisTitre: 'Frais Pédagogiques',
        fraisField: 'fraisPedagogiques',
        fraisHelp: HelpFraisPedagogique,
      },
      Annexe: {
        frais: moduleEnCours.value?.fraisAnnexesSalarie || [],
        metaFrais: metaModuleEnCours.value?.fraisAnnexesSalarie,
        fraisTitre: 'Frais Annexes',
        fraisField: 'fraisAnnexesSalarie',
        fraisHelp: HelpFraisAnnexe,
      },
    }
  })

  const modifieFrais = setupCustomUpdater<{
    mode: ModeFrais
    nature: NatureFrais
    field: keyof FraisDTO
    valeur?: string | number | null
  }>((patch, { mode, nature, field, valeur }) => {
    const modulePatch = patch.formation!.modules![activeModuleIndex.value!]!

    const fraisField = fraisDataParMode.value[mode]?.fraisField as keyof ModuleDTO

    const listeFrais = modulePatch[fraisField] as FraisDTO[]

    const fraiAModifier = listeFrais.find((f) => f.nature === nature)
    if (!fraiAModifier) {
      throw new Error(`Aucun frais trouvé pour la nature ${nature}`)
    }
    fraiAModifier[field] = valeur as any
  })

  const selectedModuleIndex = ref<number | null>(null)

  const setActiveCard = (index: number) => {
    selectedModuleIndex.value = selectedModuleIndex.value === index ? null : index
  }

  const cards = computed(() => {
    const context = useAppContextStore()
    const cardList = [
      {
        title: `Formation ${MODULE_CONFIG.Interne.label}`,
        iconName: MODULE_CONFIG.Interne.icon,
        disabled: nombreModules.value.Interne >= MODULE_CONFIG.Interne.limite,
        typeModule: 'Interne' as TypeModule,
      },
      {
        title: `Formation ${MODULE_CONFIG.Externe.label}`,
        iconName: MODULE_CONFIG.Externe.icon,
        disabled: nombreModules.value.Externe >= MODULE_CONFIG.Externe.limite,
        typeModule: 'Externe' as TypeModule,
      }
    ]
    if (context.contextCourant === TypesContextesAutorisesEnum.Prestataire && model.value?.sousDispositif !== 'Prodiat')
      return cardList.slice(1)
    return cardList
  })

  watch(
    () => afficherModale.value,
    (newVal) => newVal && (selectedModuleIndex.value = null),
  )

  return {
    // États et actions liés aux modules
    afficherModale,
    activeModuleIndex,
    nombreModules,
    nbMaxModulesAtteint,
    ajouterModule,
    définirModuleEnCours,
    supprimerModule,
    idScrollDownToSaisie,

    setActiveCard,
    cards,
    selectedModuleIndex,

    listeModules,
    moduleEnCours,
    metaModuleEnCours,
    // Actions liées aux frais
    modifieFrais,
    readonly,
    fraisDataParMode,
  }
}
